import { useCurrency, Money, GbpNote } from "../lib/currency";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BottomNav from "../components/BottomNav";
import { ProfileCompletenessCard } from "../components/ProfileCompletenessCard";
import BadgeTier from "../components/BadgeTier";
import VerificationBadges from "../components/VerificationBadges";
import VerifyCodeDialog from "../components/VerifyCodeDialog";
import StarRating from "../components/StarRating";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Wallet, Briefcase, Award, Zap, ArrowRight, UserCircle, Flame, Trophy, Megaphone, Wand2 } from "lucide-react";
import PushBell from "../components/PushBell";

function StatusPill({ status }) {
  const map = {
    pending: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
    in_progress: "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30",
    delivered: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
    released: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    disputed: "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${map[status] || map.pending}`}>
      {status.replace("_", " ")}
    </span>
  );
}

export default function CreatorDashboard() {
  const { user, profile, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const [hires, setHires] = useState([]);
  const [applications, setApplications] = useState([]);
  const [verifyDialog, setVerifyDialog] = useState({ open: false, type: "email" });

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    if (user.role !== "creator") { navigate("/brand"); return; }
    api.get("/hires").then((r) => setHires(r.data));
    api.get("/campaigns/mine/applications").then((r) => setApplications(r.data)).catch(() => {});
  }, [user, navigate, loading]);

  const verify = async (type) => {
    // Email + phone use a real code-based flow via dialog.
    if (type === "email" || type === "phone") {
      setVerifyDialog({ open: true, type });
      return;
    }
    try {
      await api.post("/creators/me/verify", { type });
      await refresh();
      toast.success(`${type} verified`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!user || !profile) return null;

  const earnings = hires
    .filter((h) => h.status === "released")
    .reduce((sum, h) => sum + (h.creator_payout || 0), 0);
  const active = hires.filter((h) => ["in_progress", "delivered"].includes(h.status));
  const verifiedCount = Object.values(profile.verifications || {}).filter(Boolean).length;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-8">
        <ProfileCompletenessCard />
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Creator dashboard</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
              Hey <span className="nexus-gradient-text">{user.name}</span>.
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/creator/profile">
              <Button variant="outline" className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full" data-testid="edit-profile-btn">
                <UserCircle className="h-4 w-4 mr-2" />
                Edit profile
              </Button>
            </Link>
            <Link to="/creator-studio">
              <Button className="nexus-gradient text-white border-0 rounded-full font-semibold" data-testid="open-studio-btn">
                <Wand2 className="h-4 w-4 mr-2" />
                Open Studio
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats bento */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Earnings</div>
              <Wallet className="h-4 w-4 text-nexus-blue" />
            </div>
            <div className="font-display font-black text-3xl nexus-gradient-text"><Money gbp={earnings} /></div>
            <div className="text-xs text-zinc-500 mt-1">after platform fee</div>
            <Link to="/creator/earnings" className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-nexus-blue hover:text-white transition-colors" data-testid="creator-earnings-link">
              View breakdown →
            </Link>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Active hires</div>
              <Briefcase className="h-4 w-4 text-nexus-pink" />
            </div>
            <div className="font-display font-black text-3xl">{active.length}</div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Reputation</div>
              <Award className="h-4 w-4 text-nexus-purple" />
            </div>
            <div className="font-display font-black text-3xl">{profile.reputation_score || 0}</div>
            <div className="mt-2"><BadgeTier tier={profile.badge_tier} size="sm" /></div>
          </div>
          <div className="p-5 rounded-2xl bg-[#0C0C10] hairline card-hover">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Star rating</div>
              <Zap className="h-4 w-4 text-[#FFEA00]" />
            </div>
            <StarRating value={profile.star_rating || 0} count={profile.review_count || 0} size="md" />
          </div>
        </div>

        {/* XP + Streak card */}
        {(() => {
          const xp = profile.xp || 0;
          const level = profile.level || 1;
          const inLevel = xp % 500;
          const pct = Math.round((inLevel / 500) * 100);
          return (
            <div className="glass rounded-2xl p-6 grid md:grid-cols-3 gap-6 items-center" data-testid="xp-widget">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                  <Trophy className="h-3.5 w-3.5 text-[#FFEA00]" /> Level
                </div>
                <div className="flex items-baseline gap-3">
                  <div className="font-display font-black text-5xl nexus-gradient-text leading-none">
                    {level}
                  </div>
                  <div className="text-zinc-300 font-semibold">{profile.level_name || "New Creator"}</div>
                </div>
                <div className="mt-3 h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full nexus-gradient transition-all duration-500" style={{ width: `${pct}%` }} data-testid="xp-progress-bar" />
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  {inLevel} / 500 XP to level {level + 1} · Total {xp} XP
                </div>
              </div>

              <div className="border-l border-white/5 md:pl-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                  <Flame className="h-3.5 w-3.5 text-nexus-pink" /> Login streak
                </div>
                <div className="font-display font-black text-5xl leading-none">
                  {profile.current_streak || 0}<span className="text-lg text-zinc-400 font-normal ml-1">days</span>
                </div>
                <div className="text-xs text-zinc-500 mt-2">
                  Longest: {profile.longest_streak || 0} days · +100 XP every 7 days
                </div>
              </div>

              <div className="border-l border-white/5 md:pl-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                  <Megaphone className="h-3.5 w-3.5 text-nexus-blue" /> Open campaigns
                </div>
                <p className="text-sm text-zinc-300 mb-3">Browse brand-posted briefs and pitch on any that fit.</p>
                <Link to="/campaigns">
                  <button className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full nexus-gradient font-semibold" data-testid="dash-open-campaigns-btn">
                    See campaigns
                  </button>
                </Link>
              </div>
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Hires */}
          <section className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Your hires</div>
            <div className="space-y-3" data-testid="creator-hires-list">
              {hires.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center">
                  <div className="font-display text-2xl mb-2">Nothing yet</div>
                  <p className="text-zinc-400 text-sm">Complete your profile so brands can find and hire you.</p>
                </div>
              ) : hires.map((h) => (
                <Link
                  key={h.id}
                  to={`/deals/${h.id}`}
                  className="block p-5 rounded-2xl bg-[#0C0C10] hairline card-hover"
                  data-testid={`hire-row-${h.id}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <StatusPill status={h.status} />
                        <div className="font-semibold">{h.package?.title || "Package"}</div>
                      </div>
                      <div className="text-sm text-zinc-400">from {h.counterparty_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-xl"><Money gbp={h.creator_payout || 0} /></div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Your payout</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-zinc-500" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Verification */}
          <aside className="space-y-6">
            <PushBell />

            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">
                Verification ({verifiedCount}/7)
              </div>
              <div className="glass rounded-2xl p-5">
                <div className="h-2 bg-white/5 rounded-full overflow-hidden mb-5">
                  <div
                    className="h-full nexus-gradient transition-all duration-500"
                    style={{ width: `${(verifiedCount / 7) * 100}%` }}
                    data-testid="verify-progress-bar"
                  />
                </div>
                <VerificationBadges verifications={profile.verifications} onVerify={verify} />
              </div>
            </div>

            {applications.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Your applications</div>
                <div className="glass rounded-2xl p-4 space-y-2" data-testid="my-applications">
                  {applications.slice(0, 5).map((a) => (
                    <Link
                      to={`/campaigns/${a.campaign_id}`}
                      key={a.id}
                      className="block p-3 rounded-lg bg-white/[0.02] hairline card-hover"
                      data-testid={`my-application-${a.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{a.campaign?.title || "Campaign"}</div>
                          <div className="text-xs text-zinc-500">{a.campaign?.brand_name}</div>
                        </div>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ${
                          a.status === "accepted" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                          a.status === "rejected" ? "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30" :
                          "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30"
                        }`}>
                          {a.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
      <BottomNav />
      <VerifyCodeDialog
        type={verifyDialog.type}
        defaultTarget={verifyDialog.type === "email" ? (user.email || "") : ""}
        open={verifyDialog.open}
        onOpenChange={(v) => setVerifyDialog((d) => ({ ...d, open: v }))}
        onVerified={() => refresh()}
      />
    </div>
  );
}
